import styled from 'styled-components';
import { createGlobalStyle } from 'styled-components';

/* ─── Layout ─────────────────────────────────────────────────────────────── */

export const Container = styled.div`
  display: flex;
  width: 100vw;
  height: 100dvh;
  overflow: hidden;
  background: #f4f6f8;

  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    background: #f4f6f8;
  }
`;

/* Left photo panel */
export const LeftContainer = styled.div`
  flex: 0 0 55%;
  position: relative;
  overflow: hidden;

  background-image: url(${({ bg }) => bg});
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.1) 35%, transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    display: none;
    flex: 0;
    width: 0;
  }
`;

/* Right teal panel */
export const RightContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 24px;
  box-sizing: border-box;
  background: #f4f6f8;

  @media (max-width: 768px) {
    flex: 1;
    padding: 24px 16px;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
    width: 100%;
    height: 100%;
    background: #f4f6f8;
  }
`;
/* White card */
export const ContentWrapper = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 48px 40px 40px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 36px 24px 32px;
  }
`;

/* ─── Typography ──────────────────────────────────────────────────────────── */

export const Title = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 16px 0 6px;
  text-align: center;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
  margin: 0 0 32px;
`;

/* ─── Form ────────────────────────────────────────────────────────────────── */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
`;

export const InputGroup = styled.div`
  position: relative;
  margin-bottom: 20px;
  width: 100%;
`;

/* Floating-label input wrapper */
export const InputLabel = styled.label`
  position: absolute;
  top: -12px;
  left: 0px;
  font-size: 14px;
  color: #61cbc9;
  background: #fff;
  padding: 0 4px;
  z-index: 1;
  pointer-events: none;
  font-family: 'Roboto', sans-serif;
`;

export const Star = styled.span`
  color: #e05c5c;
  padding-left: 1px;
`;

/* ─── Checkbox row ────────────────────────────────────────────────────────── */

export const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

export const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #555;
  line-height: 1;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
`;

/* ─── Eye icon ────────────────────────────────────────────────────────────── */

export const EyeIcon = styled.img`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

/* ─── Error popup ─────────────────────────────────────────────────────────── */

export const ErrorPopup = styled.div`
  position: fixed;
  top: 20px;
  right: -600px;
  background: rgba(255, 255, 255, 0.95);
  border-left: 4px solid #61cbc9;
  color: #333;
  padding: 14px 44px 14px 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 380px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  font-size: 14px;
  transition: right 0.45s cubic-bezier(0.16, 1, 0.3, 1);

  ${({ show }) =>
    show &&
    `
    right: 20px;
  `}

  @media (max-width: 768px) {
    top: auto;
    bottom: 20px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    ${({ show }) =>
    show &&
    `
      bottom: 20px;
    `}
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 22px;
  color: #aaa;
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  line-height: 1;
  padding: 0;

  &:hover {
    color: #61cbc9;
  }
`;

/* ─── SimpleHeader override (logo only, no height) ───────────────────────── */

export const ResponsiveKoalaFix = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

  /* Hide the SimpleHeader on the new layout — logo lives inside the card */
  .simple-header {
    display: none !important;
  }

  /* Input base styles — outlined Material-style */
  input[type="email"],
  input[type="password"],
  input[type="text"] {
    width: 100%;
    box-sizing: border-box;
    border: 1.5px solid #d0d0d0;
    border-radius: 8px;
    padding: 13px 14px;
    font-size: 15px;
    font-family: 'Roboto', sans-serif;
    color: #1a1a1a;
    background: #fff;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #bbb;
    }

    &:focus {
      border-color: #61cbc9;
    }
  }

  /* Login button */
  button[type="submit"],
  button.login-btn {
    background-color: #61cbc9;
    color: #fff;
    width: 100%;
    padding: 13px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: 'Roboto', sans-serif;
    letter-spacing: 0.01em;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
      background-color: #4db8b6;
    }

    &:active {
      background-color: #3aa5a3;
      transform: scale(0.99);
    }
  }

  /* Checkbox styled to match teal theme */
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #61cbc9;
    cursor: pointer;
  }
`;

/* ─── Unused in new layout but kept for import compatibility ──────────────── */

export const Image = styled.img`
  display: none;
`;